<script setup lang="ts">
const profileSections = [
  { name: "Personal Info", progress: 100, icon: "material-symbols:person" },
  { name: "Experience", progress: 80, icon: "material-symbols:work" },
  { name: "Education", progress: 100, icon: "material-symbols:school" },
  { name: "Video Bio", progress: 0, icon: "material-symbols:videocam" },
];
</script>

<template>
  <div class="relative">
    <!-- Main Profile Card -->
    <div class="rounded-3xl 3xl:rounded-[2rem] overflow-hidden shadow-warm-lg bg-white border border-sand/50">
      <!-- Header with Avatar -->
      <div class="bg-gradient-to-r from-secondary-dark to-secondary p-4 sm:p-6 2xl:p-8 3xl:p-10 4xl:p-12 relative">
        <div class="flex items-end gap-3 sm:gap-4 2xl:gap-5 3xl:gap-6">
          <!-- Avatar -->
          <div
            class="w-14 h-14 sm:w-20 sm:h-20 2xl:w-24 2xl:h-24 3xl:w-28 3xl:h-28 4xl:w-32 4xl:h-32 rounded-xl sm:rounded-2xl 3xl:rounded-3xl bg-white shadow-warm overflow-hidden -mb-7 sm:-mb-10 2xl:-mb-12 3xl:-mb-14 4xl:-mb-16 ring-2 sm:ring-4 ring-white"
          >
            <div class="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
              <Icon name="material-symbols:add-a-photo" class="w-6 h-6 sm:w-8 sm:h-8 2xl:w-10 2xl:h-10 3xl:w-12 3xl:h-12 text-primary/50" />
            </div>
          </div>
          <div class="text-white pb-1 sm:pb-2">
            <h3 class="font-semibold text-sm sm:text-base 2xl:text-lg 3xl:text-xl 4xl:text-2xl">Build Your Profile</h3>
            <p class="text-white/80 text-xs sm:text-sm 2xl:text-base 3xl:text-lg">Stand out to employers</p>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div
        class="p-4 pt-10 sm:p-6 sm:pt-14 2xl:p-8 2xl:pt-16 3xl:p-10 3xl:pt-20 4xl:p-12 4xl:pt-24 space-y-4 sm:space-y-5 2xl:space-y-6 3xl:space-y-8"
      >
        <!-- Progress Overview -->
        <div class="bg-cream rounded-lg sm:rounded-xl 2xl:rounded-2xl p-3 sm:p-4 2xl:p-5 3xl:p-6">
          <div class="flex items-center justify-between mb-1.5 sm:mb-2 2xl:mb-3">
            <span class="text-xs sm:text-sm 2xl:text-base 3xl:text-lg font-medium text-dark-gray">Profile Completion</span>
            <span class="text-base sm:text-lg 2xl:text-xl 3xl:text-2xl font-bold text-primary">70%</span>
          </div>
          <div class="h-1.5 sm:h-2 2xl:h-3 3xl:h-4 bg-sand rounded-full overflow-hidden">
            <div class="h-full w-[70%] bg-gradient-to-r from-primary to-savanna rounded-full transition-all duration-500"></div>
          </div>
        </div>

        <!-- Section List -->
        <div class="space-y-2 sm:space-y-3 2xl:space-y-4 3xl:space-y-5">
          <div
            v-for="section in profileSections"
            :key="section.name"
            class="flex items-center gap-2 sm:gap-3 2xl:gap-4 p-2 sm:p-3 2xl:p-4 3xl:p-5 rounded-lg sm:rounded-xl 2xl:rounded-2xl border border-sand hover:bg-cream/50 transition-colors"
          >
            <div
              class="w-8 h-8 sm:w-10 sm:h-10 2xl:w-12 2xl:h-12 3xl:w-14 3xl:h-14 4xl:w-16 4xl:h-16 rounded-lg sm:rounded-xl 2xl:rounded-2xl flex items-center justify-center flex-shrink-0"
              :class="section.progress === 100 ? 'bg-savanna/10' : section.progress > 0 ? 'bg-secondary/10' : 'bg-primary/10'"
            >
              <Icon
                :name="section.icon"
                class="w-4 h-4 sm:w-5 sm:h-5 2xl:w-6 2xl:h-6 3xl:w-7 3xl:h-7 4xl:w-8 4xl:h-8"
                :class="section.progress === 100 ? 'text-savanna' : section.progress > 0 ? 'text-secondary' : 'text-primary'"
              />
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-xs sm:text-sm 2xl:text-base 3xl:text-lg font-medium text-dark-gray">{{ section.name }}</p>
              <div class="flex items-center gap-1.5 sm:gap-2 mt-0.5 sm:mt-1">
                <div class="flex-1 h-1 sm:h-1.5 2xl:h-2 bg-sand rounded-full overflow-hidden">
                  <div
                    class="h-full rounded-full transition-all duration-500"
                    :class="section.progress === 100 ? 'bg-savanna' : section.progress > 0 ? 'bg-secondary' : 'bg-primary'"
                    :style="{ width: `${section.progress}%` }"
                  ></div>
                </div>
                <span class="text-[10px] sm:text-xs 2xl:text-sm 3xl:text-base text-medium-gray">{{ section.progress }}%</span>
              </div>
            </div>
            <Icon
              v-if="section.progress === 100"
              name="material-symbols:check-circle"
              class="w-4 h-4 sm:w-5 sm:h-5 2xl:w-6 2xl:h-6 3xl:w-7 3xl:h-7 text-savanna flex-shrink-0"
            />
            <Icon
              v-else
              name="material-symbols:arrow-forward"
              class="w-4 h-4 sm:w-5 sm:h-5 2xl:w-6 2xl:h-6 3xl:w-7 3xl:h-7 text-medium-gray flex-shrink-0"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Floating Video Tip Card -->
    <div
      class="hidden lg:block absolute -right-4 lg:-right-8 2xl:-right-12 3xl:-right-16 top-1/2 z-20 bg-white rounded-2xl 3xl:rounded-3xl shadow-warm-lg p-4 2xl:p-5 3xl:p-6 animate-gentle-float max-w-[180px] 2xl:max-w-[200px] 3xl:max-w-[240px]"
      style="animation-delay: 0.3s"
    >
      <div class="flex items-center gap-2 2xl:gap-3 mb-2 2xl:mb-3">
        <div class="w-8 h-8 2xl:w-10 2xl:h-10 3xl:w-12 3xl:h-12 rounded-lg 2xl:rounded-xl bg-primary/10 flex items-center justify-center">
          <Icon name="material-symbols:videocam" class="w-4 h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 text-primary" />
        </div>
        <span class="text-xs 2xl:text-sm 3xl:text-base font-semibold text-dark-gray">Pro Tip</span>
      </div>
      <p class="text-xs 2xl:text-sm 3xl:text-base text-medium-gray">Add a video bio to get 3x more profile views!</p>
    </div>

    <!-- Floating Stats Badge -->
    <div
      class="hidden lg:flex absolute -left-4 lg:-left-8 2xl:-left-12 bottom-1/4 z-20 bg-gradient-to-r from-gold to-gold-light rounded-full shadow-gold px-4 py-2 2xl:px-6 2xl:py-3 3xl:px-8 3xl:py-4 4xl:px-10 4xl:py-5 animate-gentle-float"
      style="animation-delay: 0.6s"
    >
      <div class="flex items-center gap-2 2xl:gap-3">
        <Icon name="material-symbols:trending-up" class="w-4 h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 text-white" />
        <span class="text-sm 2xl:text-base 3xl:text-lg font-semibold text-white">3x More Views</span>
      </div>
    </div>

    <!-- Decorative blur -->
    <div class="absolute -z-10 -top-8 -left-8 w-48 h-48 2xl:w-64 2xl:h-64 3xl:w-80 3xl:h-80 bg-secondary/10 rounded-full blur-3xl"></div>
  </div>
</template>
